import { AccountCircle } from '@mui/icons-material';
import { Avatar, Badge, Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useEffect, useState } from 'react';

import HallOfHonorAdmin from '../admin/HallOfHonorAdmin';
import { useAchievementColors } from '../hooks/useAchievementColors';
import { AVATAR_SIZE, GRADIENT_CUTOFF, PADDING_TOP } from '../utils/constants';
import AchievementGrid from './AchievementGrid';
import AchievementsTimeline from './AchievementsTimeline';

const styles = {
    icon: {
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        mb: 1,
        color: 'lightgray',
    },
};

const HallOfHonor = ({
    userInfo,
    userRole,
    isLoadingRole,
    getUserRoles,
    // Achievement data
    allAchievements,
    lockedAchievements,
    unlockedAchievements,
    completedAchievements,
    achievementsTypes = [],
    achievementsTimeline,
    // Loading states
    isAllAchievementsLoading,
    isLockedAchievementsLoading,
    isUnlockedAchievementsLoading,
    isCompletedAchievementsLoading,
    isAchievementsTimelineLoading,
    // Actions
    getAllAchievements,
    getLockedAchievements,
    getUnlockedAchievements,
    getAchievementsTypes,
    getAchievementsTimeline,
    // Pet related
    selectedPetId,
    getUserSelectedPet,
    patchUserSelectedPet
}) => {
    // Use dynamic achievement colors
    const getAchievementColors = useAchievementColors(achievementsTypes);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Load achievements data on component mount
    useEffect(() => {
        getAllAchievements();
        getLockedAchievements();
        getUnlockedAchievements();
        getAchievementsTypes();
        getUserRoles();
        getAchievementsTimeline();
        getUserSelectedPet();
    }, [getAllAchievements, getLockedAchievements, getUnlockedAchievements, getAchievementsTypes, getUserRoles, getAchievementsTimeline, getUserSelectedPet]);

    // Calculate achievement progress
    const achievementsUnlocked = unlockedAchievements?.length || 0;
    const achievementsLocked = lockedAchievements?.length || 0;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Prepare achievements for the "Unlocked" tab
    const unlockedAndCompleted = [...(unlockedAchievements || []), ...(completedAchievements || [])]
        .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));

    // Determine which tabs to show
    const showLockedTab = achievementsLocked > 0;
    const tabs = [
        {
            label: 'Unlocked',
            icon: 'lock-open',
            count: achievementsUnlocked,
            content: (
                <AchievementGrid
                    achievements={unlockedAndCompleted}
                    isLoading={isUnlockedAchievementsLoading || isCompletedAchievementsLoading}
                    getAchievementColors={getAchievementColors}
                    isLocked={false}
                    selectedPetId={selectedPetId}
                    patchUserSelectedPet={patchUserSelectedPet}
                />
            )
        }
    ];

    // Add locked tab only if there are locked achievements
    if (showLockedTab) {
        tabs.push({
            label: 'To Unlock',
            icon: 'lock-closed',
            count: achievementsLocked,
            content: (
                <AchievementGrid
                    achievements={lockedAchievements}
                    isLoading={isLockedAchievementsLoading}
                    getAchievementColors={getAchievementColors}
                    isLocked={true}
                />
            )
        });
    }

    return (
        <Box sx={{ px: 3, py: 1 }}>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid
                    item
                    justifyContent="center"
                    justifyItems="center"
                    sx={{
                        background: `linear-gradient(to bottom, #1a242d 0px, #1a2c3c ${GRADIENT_CUTOFF}px, #fff ${GRADIENT_CUTOFF}px, #fff 100%)`,
                        borderRadius: 3,
                        boxShadow: 2,
                        px: 2,
                        mt: 1,
                        position: 'relative',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    xs={12}
                    sm={9}
                >
                    {/* Top banner: title + profile picture */}
                    <Grid item xs={12}>
                        <Box sx={{ width: '100%', pt: `${PADDING_TOP}px`, pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'transparent' }}>
                            {userInfo.picture ? (
                                <Avatar src={userInfo.picture} alt="profile" sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, mb: 1 }} />
                            ) : (
                                <AccountCircle style={styles.icon} fontSize="large" />
                            )}
                        </Box>
                    </Grid>

                    {/* Profile Progress */}
                    <Grid item xs={12} container direction="column" justifyContent="center" alignItems="center" mb={-1} mt={-4} rowSpacing={0}>
                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5, spacing: 0, flexDirection: 'column' }}>
                                <Typography variant="h2" style={{ fontWeight: 500 }} color="primary">
                                    {achievementsUnlocked}
                                </Typography>
                                <Typography variant="caption" style={{ fontWeight: 300, color: 'gray' }}>
                                    Total achievements
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Tabs */}
                    <Grid item xs={12} sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, mt: 0, width: '100%' }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                sx={{
                                    minHeight: 'unset',
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        minHeight: 'unset',
                                        py: 1.5,
                                    }
                                }}
                            >
                                {tabs.map((tab, index) => (
                                    <Tab
                                        key={`hall-of-honor-tab-${tab.label}-${index}`}
                                        label={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <IconComponent iconName={tab.icon} style={{ fontSize: 16 }} />
                                                {tab.label}
                                                {tab.count > 0 && (
                                                    <Badge
                                                        badgeContent={tab.count}
                                                        color="primary"
                                                        sx={{
                                                            '& .MuiBadge-badge': {
                                                                fontSize: '10px',
                                                                height: '19px',
                                                                minWidth: '16px',
                                                                borderRadius: '10px',
                                                                position: 'relative',
                                                                transform: 'none',
                                                                top: 'unset',
                                                                right: 'unset',
                                                                ml: 0.5
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    </Grid>
                    {/* Tab Content */}
                    <Grid item xs={12} sx={{ px: 3, pt: 2, pb: 4, minWidth: 0, flex: 1 }}>
                        {tabs[activeTab]?.content}
                    </Grid>
                </Grid>

                {/* Achievements Timeline */}
                <Grid item sx={{ borderRadius: 2, mt: 1, display: 'flex', flexDirection: 'column' }} xs={12} sm={3}>
                    <AchievementsTimeline achievementsTimeline={achievementsTimeline} isAchievementsTimelineLoading={isAchievementsTimelineLoading} />
                </Grid>

                {/* Admin Panel */}
                {userRole?.hall_honor_admin && (
                    <Grid item xs={12} sx={{ pl: '0 !important' }}>
                        <HallOfHonorAdmin hall_honor_admin={userRole?.hall_honor_admin} />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export { HallOfHonor };